import * as angular from "angular";
import { CampaignDataProvider } from "../data-provider/campaign.dp";
import { EffortDataProvider } from "../data-provider/effort.dp";
import { DataProvider } from "../data-provider/data-provider";
import { UserDataProvider } from "../data-provider/user.dp";
import { SocketService } from "../env-set-up/socket";
import { Campaign } from "../models/campaign.model";
import { Organisation } from "../models/organisation.model";
import { User } from "../models/user.model";
import { PermissionCheck } from "../shared/permission";
import { PrivacyNotice } from "../models/privacy-notice.model";
import { DialogUtils } from "./dialog.helper";
import { PrivacyNoticeDataProvider } from "../data-provider/privacy-notice.dp";

export class Utils {
    private readonly $window: any;
    private readonly $location: any;
    private readonly $cookies: any;
    private readonly dialog: DialogUtils;

    private readonly campaignDataProvider: CampaignDataProvider;
    private readonly permissionProvider: PermissionCheck;
    private readonly socketService: SocketService;
    private readonly dataProvider: DataProvider;
    private readonly userDataProvider: UserDataProvider;
    private readonly effortDataProvider: EffortDataProvider;
    private readonly privacyNoticeDataProvider: PrivacyNoticeDataProvider;

    public campaign: Campaign;
    public isLeaderView: boolean;

    constructor(
        $window: any,
        __app_type: string,
        $location: any,
        $cookies: any,
        DialogUtils: DialogUtils,
        CampaignDataProvider: CampaignDataProvider,
        PermissionCheck: PermissionCheck,
        SocketService: SocketService,
        DataProvider: DataProvider,
        EffortDataProvider: EffortDataProvider,
        UserDataProvider: UserDataProvider,
        PrivacyNoticeDataProvider: PrivacyNoticeDataProvider
    ) {
        this.$window = $window;
        this.$location = $location;
        this.$cookies = $cookies;
        this.dialog = DialogUtils;

        this.campaignDataProvider = CampaignDataProvider;
        this.permissionProvider = PermissionCheck;
        this.socketService = SocketService;
        this.dataProvider = DataProvider;
        this.effortDataProvider = EffortDataProvider;
        this.userDataProvider = UserDataProvider;
        this.privacyNoticeDataProvider = PrivacyNoticeDataProvider;

        this.campaign = this.campaignDataProvider.activeCampaign;
        this.isLeaderView = __app_type === "LEADER";
    }

    activeUid(): string | number | undefined {
        return this.$cookies.get("uid");
    }

    navigateBack(): void {
        this.$window.history.back();
    }

    runWootric(): void {
        if (this.$window.wootricSettings) {
            let url = this.$location.url();
            const pageName = url.split("?")[0].substring(1);
            this.$window.wootricSettings.properties.page = pageName ?? null;
            this.$window.wootric("run");
        }
    }

    accessCheck(): void {
        if (this.isLeaderView) {
            this.campaignDataProvider
                .getOrganisations()
                .then((organisation: Organisation) => {
                    if (
                        !organisation ||
                        !organisation.permissionid ||
                        !this.permissionProvider.hasPermission(
                            "LEADER_PAGES",
                            null,
                            organisation.permissionid
                        )
                    ) {
                        this.$window.location = "/#/";
                    }
                });
        } else {
            this.userDataProvider
                .getUser(this.$cookies.get("uid"))
                .then((user: User) => {
                    let campaign: Campaign =
                        this.campaignDataProvider.getActiveCampaign();
                    if (!user.isAdmin() && campaign.paymentFailed()) {
                        this.$window.location = "/#/confirmpayment";
                    }

                    const badSubscriptionStatuses = [
                        "past_due",
                        "unpaid",
                        "canceled",
                        "incomplete",
                        "incomplete_expired",
                        "trialing",
                        "ended",
                    ];
                    if (
                        !this.permissionProvider.hasPermission(
                            "DASHBOARD_PAGES"
                        ) &&
                        ["/", "/profile"].indexOf(this.$location.path()) == -1
                    ) {
                        this.$location.path("/");
                    } else if (
                        ((this.campaign.campaignstatusid == 2 &&
                            this.campaign.trial_days <= 0 &&
                            badSubscriptionStatuses.indexOf(
                                this.campaign.subscriptionstatus
                            ) > -1) ||
                            this.campaign.permissionid === 7 ||
                            this.campaign.campaignstatusid === 3) &&
                        this.$location.path().indexOf("/profile") == -1
                    ) {
                        if (!user.isAdmin()) {
                            this.$location.path("/expired");
                        }
                    } else if (this.$cookies.get("noAccess") == "true") {
                        this.$location.path("/no_access");
                    } else if (
                        this.$cookies.get("privacyNotice") == "declined"
                    ) {
                        this.$location.path("/declined");
                    } else if (
                        this.campaign.permissionid == 5 ||
                        this.campaign.permissionid == 6
                    ) {
                        const pages = [
                            "/people",
                            "/lists",
                            "/houses",
                            "/streets",
                            "/appointments",
                            "/events",
                            "/events/info",
                            "/map",
                            "/profile",
                            "/talkingpoint",
                            "/getting-started",
                        ];

                        const result = pages.filter((page: string) => {
                            return (
                                typeof page == "string" &&
                                this.$location.path().startsWith(page)
                            );
                        });

                        if (this.$location.path() !== "/" && !result.length) {
                            this.$location.path("/");
                        }
                    } else if (
                        this.$location.path() == "/teams" &&
                        !this.permissionProvider.hasPermission("TEAM_PAGE")
                    ) {
                        this.$location.path("/");
                    } else if (
                        (this.$location.path() == "/voterfiles" ||
                            this.$location.path() == "/voterfiles/conflicts") &&
                        !this.permissionProvider.hasPermission("IMPORT")
                    ) {
                        this.$location.path("/");
                    } else if (
                        this.$location.path() == "/privacy" &&
                        !this.permissionProvider.hasPermission(
                            "PRIVACY_COMPLIANCE"
                        )
                    ) {
                        this.$location.path("/");
                    }
                });
        }
    }

    openSupport($event: any): void {
        $event.preventDefault();
        this.$window.Intercom("showNewMessage");
    }

    initNewCampaignData(newCampaign: Campaign): void {
        this.campaign = newCampaign;
        this.socketService.changeTopic(newCampaign.campaignid, "campaign");
        this.dataProvider.clearAll();
        this.permissionProvider.setPermission(newCampaign.permissionid);
        this.effortDataProvider.getAllEfforts();
    }

    bindFunction(fn: any, bindingContext: any): any {
        let boundFn = fn.bind(bindingContext);
        boundFn.originalFn = fn;
        return boundFn;
    }

    acknowledgePrivacyNoticeDialog(
        privacyNotice: PrivacyNotice,
        $event?: any
    ): void {
        console.log("setting here ", privacyNotice);

        this.dialog
            .show({
                component: "vc-user-privacy-notice",
                ariaLabel: "Acknowledge Privacy Notice",
                locals: {
                    privacyNotice: privacyNotice,
                },
                targetEvent: $event,
                clickOutsideToClose: false,
            })
            .then(() => {
                if (this.$cookies.get("privacyNotice") == "declined") {
                    this.$location.path("/declined");
                } else {
                    this.privacyNoticeDataProvider.clearAll();
                    this.$location.path("/");
                }
            });
    }

    getNumberFromName(name: string): number {
        let result = 0;
        let nameUpperCase = name.toUpperCase();
        for (let i = 0; i < nameUpperCase.length; i++) {
            let numericValue = nameUpperCase.charCodeAt(i) - 65 + 1;
            result = result * 26 + numericValue;
        }
        return result - 1;
    }
    getNameFromNumber(num: number): string {
        if (num >= 237) {
            num++;
        }
        let numeric = num % 26;
        let letter = String.fromCharCode(65 + numeric);
        let num2 = parseInt(`${num / 26}`);
        if (num2 > 0) {
            return this.getNameFromNumber(num2 - 1) + letter;
        } else {
            return letter;
        }
    }
}

angular.module("vcHelper").service("Utils", Utils);
